import React, { useState } from 'react';
import { Smartphone, Zap, Users, Award, Sparkles, Send } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import CursorGlow from '../../components/CursorGlow';
import ScrollProgress from '../../components/ScrollProgress';

const MobileAppDevelopment = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    budget: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    toast({
      title: "Harika! 🚀",
      description: "Ekibimiz 24 saat içinde sizinle iletişime geçecek.",
    });
    setFormData({ name: '', email: '', phone: '', projectType: '', budget: '', message: '' });
  };

  const features = [
    {
      icon: Smartphone,
      title: 'iOS & Android Uygulama',
      description: 'React Native veya Native ile performanslı, kullanıcı dostu mobil uygulamalar'
    },
    {
      icon: Zap,
      title: 'Hızlı Geliştirme',
      description: 'Agile metodoloji ile 8-12 hafta içinde MVP teslimi'
    },
    {
      icon: Users,
      title: 'Kullanıcı Odaklı Tasarım',
      description: 'UX araştırması ve kullanıcı testleri ile optimize edilmiş deneyim'
    },
    {
      icon: Award,
      title: 'App Store Optimizasyonu',
      description: 'Yayınlama, ASO ve ilk kullanıcı kazanımı desteği'
    }
  ];

  const process = [
    { step: '01', title: 'Keşif & Planlama', desc: 'İhtiyaç analizi, wireframe ve teknik mimari' },
    { step: '02', title: 'UI/UX Tasarım', desc: 'Kullanıcı arayüzü ve deneyim tasarımı' },
    { step: '03', title: 'Geliştirme', desc: 'Sprint bazlı kodlama ve haftalık demolar' },
    { step: '04', title: 'Test & Yayınlama', desc: 'QA, beta test ve store yayınlama' }
  ];

  const technologies = ['React Native', 'Swift', 'Kotlin', 'Flutter', 'Firebase', 'AWS', 'Node.js', 'MongoDB'];

  return (
    <div className="min-h-screen bg-slate-900">
      <CursorGlow />
      <ScrollProgress />
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute w-[600px] h-[600px] bg-cyan-500/20 rounded-full blur-3xl top-0 right-1/4 animate-pulse"></div>
          <div className="absolute w-[500px] h-[500px] bg-teal-500/20 rounded-full blur-3xl bottom-0 left-1/4 animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full mb-6">
                <Sparkles className="w-4 h-4 text-cyan-400 animate-spin-slow" />
                <span className="text-cyan-400 text-sm font-medium">Mobil Uygulama Geliştirme</span>
              </div>

              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
                Fikrinizi
                <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent"> Milyonların </span>
                Cebine Taşıyın
              </h1>

              <p className="text-xl text-gray-400 mb-8 leading-relaxed">
                iOS ve Android için performanslı, ölçeklenebilir ve kullanıcı dostu mobil uygulamalar geliştiriyoruz. MVP&apos;den kurumsal uygulamalara kadar her ölçekte çözüm.
              </p>

              <div className="flex flex-wrap gap-4">
                <Button
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white px-8 py-6 text-lg hover:scale-105 transition-all duration-300"
                  data-testid="service-cta-button"
                >
                  Ücretsiz Görüşme
                </Button>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=800&h=600&fit=crop"
                alt="Mobile App Development"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-white mb-16">
            Neden <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent">RUYAN</span>?
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="group bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 hover:border-cyan-500/50 transition-all duration-500 hover:transform hover:translateY(-4px) hover:shadow-2xl hover:shadow-cyan-500/20"
                style={{
                  animation: 'fade-in-up 0.8s ease-out both',
                  animationDelay: `${index * 0.15}s`
                }}
              >
                {/* Animated Icon Container */}
                <div className="relative w-14 h-14 mb-4">
                  {/* Orbital rings */}
                  <div className="absolute inset-0 border-2 border-transparent border-t-cyan-500/60 border-r-teal-500/40 rounded-full animate-spin" style={{ animationDuration: '3s' }}></div>
                  <div className="absolute inset-0 border-2 border-transparent border-t-teal-500/60 border-r-cyan-500/40 rounded-full animate-spin" style={{ animationDuration: '4s', animationDirection: 'reverse', transform: 'scale(0.8)' }}></div>
                  
                  {/* Icon background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-xl flex items-center justify-center transition-transform duration-500 group-hover:rotate-12 group-hover:scale-110">
                    <feature.icon className="w-7 h-7 text-white transition-transform duration-500 group-hover:rotate-[-12deg]" />
                  </div>

                  {/* Glow effect */}
                  <div className="absolute inset-0 bg-cyan-500/50 rounded-xl blur-xl opacity-0 group-hover:opacity-70 transition-opacity duration-500"></div>
                </div>

                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition-colors duration-300">{feature.title}</h3>
                <p className="text-gray-400 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-white mb-16">
            <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent">Geliştirme Süreci</span>
          </h2>

          <div className="grid md:grid-cols-4 gap-8">
            {process.map((item, index) => (
              <div key={index} className="relative">
                <div className="text-6xl font-bold text-cyan-500/20 mb-4">{item.step}</div>
                <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
                {index < process.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-cyan-500/50 to-transparent"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center text-white mb-16">
            Kullandığımız <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent">Teknolojiler</span>
          </h2>

          <div className="flex flex-wrap justify-center gap-4">
            {technologies.map((tech, index) => (
              <div key={index} className="px-6 py-3 bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-xl text-gray-300 hover:border-cyan-500/50 hover:text-cyan-400 hover:scale-110 transition-all duration-300">
                {tech}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Form */}
      <section id="contact-form" className="py-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute w-[800px] h-[800px] bg-cyan-500/10 rounded-full blur-3xl top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-pulse"></div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="bg-slate-800/60 backdrop-blur-xl border border-cyan-500/30 rounded-3xl p-12">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-white mb-4">
                Hayalinizdeki Uygulamayı
                <span className="bg-gradient-to-r from-cyan-400 to-teal-400 bg-clip-text text-transparent"> Birlikte Yaratalım! </span>
              </h2>
              <p className="text-gray-400 text-lg">
                Hemen başlayın - ücretsiz keşif görüşmesi ve proje teklifi için formu doldurun
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name" className="text-gray-300 mb-2 block">Adınız Soyadınız *</Label>
                  <Input
                    id="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-slate-900/50 border-cyan-500/30 text-white h-12 rounded-xl focus:border-cyan-500 focus:ring-cyan-500 transition-all"
                    placeholder="Ahmet Yılmaz"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-gray-300 mb-2 block">E-posta *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-slate-900/50 border-cyan-500/30 text-white h-12 rounded-xl focus:border-cyan-500 focus:ring-cyan-500 transition-all"
                    placeholder="ahmet@sirket.com"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="phone" className="text-gray-300 mb-2 block">Telefon</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="bg-slate-900/50 border-cyan-500/30 text-white h-12 rounded-xl focus:border-cyan-500 focus:ring-cyan-500 transition-all"
                    placeholder="+90 XXX XXX XX XX"
                  />
                </div>
                <div>
                  <Label htmlFor="budget" className="text-gray-300 mb-2 block">Bütçe Aralığı</Label>
                  <select
                    id="budget"
                    value={formData.budget}
                    onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                    className="w-full bg-slate-900/50 border border-cyan-500/30 text-white h-12 rounded-xl px-4 focus:border-cyan-500 focus:ring-cyan-500 transition-all"
                  >
                    <option value="">Seçiniz</option>
                    <option value="10k-25k">€10K - €25K</option>
                    <option value="25k-50k">€25K - €50K</option>
                    <option value="50k+">€50K+</option>
                  </select>
                </div>
              </div>

              <div>
                <Label htmlFor="message" className="text-gray-300 mb-2 block">Proje Detayları *</Label>
                <Textarea
                  id="message"
                  required
                  rows={5}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="bg-slate-900/50 border-cyan-500/30 text-white rounded-xl resize-none focus:border-cyan-500 focus:ring-cyan-500 transition-all"
                  placeholder="Uygulamanız hakkında bize bilgi verin: Ne yapmak istiyorsunuz? Hedef kitleniz kim? Özel özellikler?"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white py-6 text-lg font-semibold rounded-xl group hover:scale-105 transition-all duration-300 hover:shadow-2xl hover:shadow-cyan-500/50"
              >
                Ücretsiz Teklif Al
                <Send className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>

              <p className="text-center text-sm text-gray-500">
                ⚡ 24 saat içinde yanıt • 💎 Ücretsiz keşif görüşmesi • 🚀 Hızlı başlangıç
              </p>
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MobileAppDevelopment;